import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import adminConfigApi from "../../api/admin/AdminConfigApi";
import { AllowIpDto, ConfigDto, FwocDto } from "../../../types/adminConfig";
import { useTranslation } from "react-i18next";
import {
  getAdminInfoFromLocalStorage,
  setAdminInfoFromLocalStorage,
} from "../../../shared/utils/auth";
import { LoginDto } from "../../../types/auth";

const initialState = {
  config: {
    activeHtmlWrapping: false,
    allowMacOS: false,
    allowMobile: true,
    autoUnwrappingPermission: false,
    companyName: null,
    externalUserViewType: "MY_LIST",
    fileOwnerSelectPermission: "FILEUPLOADER",
    fileUploadMaxSizeMB: 200,
    fileUploadPermission: "EXTERNAL",
    licenseAlertPercentage: 80,
    mobilePushKeyType: "2022",
    restorePermission: "MASTER",
    sentMail: false,
    passwordPolicyDTO: {
      minLength: 6,
      maxLength: 30,
    },
    folderMoveOwner: false,
    serviceType: "CLOUD",
    unwrappingPermission: "MASTER",
    userOtpType: "NONE",
    watermark: "ALL",
    watermarkFontSize: 16,
    watermarkOpacity: 0.2,
    watermarkPattern:
      "${username}(${userid}) / ${contentname} / ${currenttime} / ",
    watermarkRotate: -30,
    watermarkTop: 59,
    webOfficeAllowUserType: "ALL",
    webOfficeType: "COLLABORA",
    workgroupCreatePermission: "INTERNAL",
    workgroupHeadSelectPermission: "@@WORKGROUPCREATOR",
    lastUpdateTime: 0,
    notificationPreferencePolicyDTO: {
      usingWorkgroupInvitedAlert: true,
      usingFileAlert: true,
      fileAlert: ["fileshare", "memo", "revision"],
      usingReadRequestAlert: true,
      readRequestAlert: ["readRequest", "readRequestConfirm"],
    },
    workgroupTreeViewPermission: "ALL",
    workgroupTreeCreatePermission: "MASTER",
    workgroupPermissionDTO: {
      changeCode: "CREATE",
      uniqueId: null,
      workgroupCode: null,
      syncId: "NONE",
      targetType: "WORKGROUP",
      targetValue: null,
      authType: "LOGIN",
      policyType: "TEMPLATE",
      policyCode: null,
      updateTime: 1,
      createUser: "",
      updateUser: "",
      policy: {
        startTime: 0,
        allowDays: 0,
        allowDaysTimeZone: 9.0,
        allowViewCount: 0,
        currentViewCount: 0,
        allowDownloadCount: 0,
        currentDownloadCount: 0,
        fileShareLinkPolicy: {
          authType: "LOGIN",
          password: "",
          urlAction: "WEBUSING_AND_DOWNLOAD",
          allowDays: 61,
          allowDaysTimeZone: 9,
        },
        drmPerm: ["view", "print", "print_screen", "extract"],
        webView: true,
        revision: true,
        download: true,
        wrapping: true,
      },
    },
  } as ConfigDto,
  fwoc: {
    defaultWebOffice: "",
    oosUse: false,
    oosUrl: false,
    office365Use: false,
    office365Url: "",
    collaboraUse: false,
    collaboraUrl: "",
    tfsiUse: false,
    tfsiUrl: "",
  } as FwocDto,
  allowIp: {
    allowIp: [] as AllowIpDto[],
    errorMessage: "",
    errorCode: "",
  },
};

export const trunkGetConfig = createAsyncThunk(
  "wapi/admins/config/get",
  async () => {
    const result = await adminConfigApi.getConfig();
    return result;
  }
);

export const trunkSetConfig = createAsyncThunk(
  "wapi/admins/config/set",
  async (config: ConfigDto) => {
    const result = await adminConfigApi.setConfig(config);
    return result;
  }
);

export const trunkGetFwocConfig = createAsyncThunk(
  "wapi/admins/config/fwoc",
  async () => {
    const result = await adminConfigApi.getFwocConfig();
    return result;
  }
);
export const trunkGetIpConfig = createAsyncThunk(
  "wapi/admins/config/access/control/get",
  async () => {
    const result = await adminConfigApi.getIpConfig();
    return result;
  }
);
export const trunkSetIpConfig = createAsyncThunk(
  "wapi/admins/config/access/control/set",
  async (newIp: AllowIpDto[]) => {
    const ipResult = await adminConfigApi.setIpConfig(newIp);
    return ipResult;
  }
);

const adminConfigSlice = createSlice({
  name: "adminConfig",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(trunkGetConfig.fulfilled, (state, action) => {
        state.config = action.payload;
      })
      .addCase(trunkGetConfig.rejected, (state, action) => {
        state.config.errorMessage = action.error.message;
      })
      .addCase(trunkSetConfig.fulfilled, (state, action) => {
        state.config = action.payload;
        if (
          getAdminInfoFromLocalStorage() &&
          window.eco.serviceType !== "OCI"
        ) {
          const user = getAdminInfoFromLocalStorage() ?? "";
          const loginDto: LoginDto = JSON.parse(user);

          setAdminInfoFromLocalStorage({
            ...loginDto,
            ecoCommonConfig: action.payload,
          });
        }
      })
      .addCase(trunkSetConfig.rejected, (state, action) => {
        state.config.errorMessage = "admin.common.alert.fail";
      });
    builder
      .addCase(trunkGetFwocConfig.fulfilled, (state, action) => {
        state.fwoc = action.payload;
      })
      .addCase(trunkGetFwocConfig.rejected, (state, action) => {
        state.fwoc.errorMessage = action.error.message;
      });
    builder
      .addCase(trunkGetIpConfig.fulfilled, (state, action) => {
        state.allowIp.allowIp = action.payload;
      })
      .addCase(trunkGetIpConfig.rejected, (state, action) => {
        if (action.error.message && action.error.code) {
          state.allowIp.errorMessage = action.error.message;
          state.allowIp.errorCode = action.error.code;
        }
      })
      .addCase(trunkSetIpConfig.fulfilled, (state, action) => {
        state.allowIp.allowIp = action.payload;
      })
      .addCase(trunkSetIpConfig.rejected, (state, action) => {
        if (action.error.message && action.error.code) {
          state.allowIp.errorMessage = action.error.message;
          state.allowIp.errorCode = action.error.code;
        }
      });
  },
});

export const adminConfigActions = adminConfigSlice.actions;
export default adminConfigSlice.reducer;
